import { z } from 'zod'
import { trainerSocialMediaSummarySchema } from './trainer-social-media-read'
import { TRAINER_CONTRACT_VERSION, trainerWriteSideEffectsSchema } from './trainer-event-read'

const nullableMutationString = z.string().nullable().optional()

/**
 * Social writes retain the existing handler's legacy validation boundary. The
 * route still owns required/non-empty checks, URL checks, and exact error
 * messages; these schemas describe the accepted transport shape for clients
 * and independently authored contract fixtures without tightening that route.
 */
const socialMutationFields = {
  name: z.string(),
  platform: z.string(),
  type: z.string(),
  description: nullableMutationString,
  image_url: nullableMutationString,
  source_url: nullableMutationString,
  editable: z.boolean().nullable().optional(),
} as const

export const trainerSocialMediaCreateRequestSchema = z.object({
  ...socialMutationFields,
  // The handler validates this provider/upload branch with the Uploads
  // contract. Keep it opaque here so the shared contract does not tighten the
  // existing provider-specific 400 responses.
  upload: z.unknown().optional(),
}).passthrough()

export const trainerSocialMediaUpdateRequestSchema = z.object({
  social_media_id: z.unknown().optional(),
  ...socialMutationFields,
}).passthrough()

export const trainerSocialMediaStatusRequestSchema = z.object({
  active: z.boolean(),
}).passthrough()

const trainerSocialMediaWriteResponseShape = {
  contract_version: z.literal(TRAINER_CONTRACT_VERSION),
  socialMedia: trainerSocialMediaSummarySchema,
  sideEffects: trainerWriteSideEffectsSchema.passthrough(),
}

const trainerSocialMediaCreateSideEffectsSchema = trainerWriteSideEffectsSchema.extend({
  uploadTouched: z.boolean(),
  r2Touched: z.boolean().optional(),
  providerHeadersReturned: z.boolean().optional(),
  objectKeyReturned: z.boolean().optional(),
}).passthrough()

export const trainerSocialMediaCreateResponseSchema = z.object({
  ...trainerSocialMediaWriteResponseShape,
  sideEffects: trainerSocialMediaCreateSideEffectsSchema,
}).passthrough()
export const trainerSocialMediaUpdateResponseSchema = z.object(trainerSocialMediaWriteResponseShape).passthrough()

export const trainerSocialMediaArchiveLifecycleSchema = z.object({
  operation: z.enum(['archive', 'restore']),
  sourceOfTruth: z.literal('DB1'),
  statusField: z.literal('trainer_social_media.active'),
  active: z.boolean(),
  retentionMode: z.literal('db1_soft_archive'),
  providerAssetTouched: z.literal(false),
  uploadTouched: z.literal(false),
  destructiveCleanup: z.literal(false),
  rawProviderPayloadReturned: z.literal(false),
})

export const trainerSocialMediaStatusResponseSchema = z.object({
  ...trainerSocialMediaWriteResponseShape,
  idempotentReplay: z.boolean(),
  socialArchiveLifecycle: trainerSocialMediaArchiveLifecycleSchema,
}).passthrough()

export type TrainerSocialMediaCreateRequest = z.infer<typeof trainerSocialMediaCreateRequestSchema>
export type TrainerSocialMediaUpdateRequest = z.infer<typeof trainerSocialMediaUpdateRequestSchema>
export type TrainerSocialMediaStatusRequest = z.infer<typeof trainerSocialMediaStatusRequestSchema>
export type TrainerSocialMediaCreateResponse = z.infer<typeof trainerSocialMediaCreateResponseSchema>
export type TrainerSocialMediaUpdateResponse = z.infer<typeof trainerSocialMediaUpdateResponseSchema>
export type TrainerSocialMediaStatusResponse = z.infer<typeof trainerSocialMediaStatusResponseSchema>
